import { useMemo } from "react";
import type { KnowledgeArticle } from "@/types/knowledgeBase";
import { knowledgeCategories } from "@/types/knowledgeBase";

interface ArticleDetailProps {
  article: KnowledgeArticle;
  allArticles: KnowledgeArticle[];
  onCopyReply: (text: string) => void;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

export default function ArticleDetail({
  article,
  allArticles,
  onCopyReply,
  isAdmin,
  onEdit,
  onDelete,
}: ArticleDetailProps) {
  const getCategoryLabel = (id: string) => {
    const found = knowledgeCategories.find((c) => c.id === id);
    return found ? found.label : id;
  };

  const relatedArticles = useMemo(() => {
    return article.relatedIds
      .map((id) => allArticles.find((a) => a.article_code === id))
      .filter(Boolean) as KnowledgeArticle[];
  }, [article.relatedIds, allArticles]);

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb + Admin Actions */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-foreground-400">
          <span className="font-mono">{article.article_code}</span>
          <i className="ri-arrow-right-s-line" />
          <span>{getCategoryLabel(article.category)}</span>
          <i className="ri-arrow-right-s-line" />
          <span>{article.subCategory}</span>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-50 cursor-pointer transition-colors"
              title="編輯"
            >
              <i className="ri-edit-line text-sm" />
            </button>
            <button
              onClick={onDelete}
              className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-accent-600 hover:bg-accent-50 cursor-pointer transition-colors"
              title="刪除"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        )}
      </div>

      {/* Title */}
      <div className="px-5 pb-3">
        <h2
          className="text-base font-bold text-foreground-900 leading-snug"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {article.title}
        </h2>
      </div>

      {/* Tags */}
      <div className="px-5 pb-4 flex flex-wrap gap-1.5">
        {article.tags.map((tag) => (
          <span
            key={tag}
            className="text-[10px] px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 border border-background-200"
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 pb-5 space-y-5">
        {/* Suggested Reply */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-message-3-line text-primary-500 text-sm" />
            <span className="text-xs font-semibold text-foreground-700">
              建議回覆話術
            </span>
          </div>
          <div className="bg-primary-50/60 border border-primary-200/60 rounded-xl p-3.5 relative group">
            <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-wrap">
              {article.suggestedReply}
            </p>
            <button
              onClick={() => onCopyReply(article.suggestedReply)}
              className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md bg-background-50 border border-background-200 hover:bg-background-100 cursor-pointer"
              title="複製話術"
            >
              <i className="ri-file-copy-line text-foreground-500 text-xs" />
            </button>
          </div>
        </div>

        {/* SOP */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-list-check-2 text-secondary-500 text-sm" />
            <span className="text-xs font-semibold text-foreground-700">
              員工 SOP
            </span>
          </div>
          <ol className="space-y-2">
            {article.sop.map((step, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2.5 text-sm text-foreground-700 leading-relaxed"
              >
                <span className="w-5 h-5 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                  {idx + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Departments */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <i className="ri-building-2-line text-accent-500 text-sm" />
            <span className="text-xs font-semibold text-foreground-700">
              對應部門
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {article.departments.map((dept) => (
              <span
                key={dept}
                className="text-xs px-2.5 py-1 rounded-full bg-accent-50 text-accent-700 border border-accent-200"
              >
                #{dept}
              </span>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2 pt-2">
          <button
            onClick={() => onCopyReply(article.suggestedReply)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-copy-line" />
            複製話術
          </button>
          <button
            onClick={() => onCopyReply(article.suggestedReply)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm font-medium hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-chat-3-line" />
            登錄聊天框
          </button>
          <button
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-background-200 text-foreground-500 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-thumb-down-line" />
            回覆答不好
          </button>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <div className="pt-2">
            <div className="flex items-center gap-1.5 mb-2">
              <i className="ri-links-line text-foreground-400 text-sm" />
              <span className="text-xs font-semibold text-foreground-500">
                相關推薦
              </span>
            </div>
            <div className="space-y-2">
              {relatedArticles.map((ra) => (
                <div
                  key={ra.id}
                  className="flex items-start gap-2 p-2.5 rounded-lg border border-background-200 bg-background-50 hover:bg-background-100 transition-colors"
                >
                  <span className="text-[10px] font-mono text-foreground-400 shrink-0 mt-0.5">
                    {ra.article_code}
                  </span>
                  <div>
                    <p className="text-xs font-medium text-foreground-800 leading-snug">
                      {ra.title}
                    </p>
                    <p className="text-[10px] text-foreground-400 mt-0.5 line-clamp-1">
                      {ra.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}